import { cloudStorage } from './cloudAppwriteClient';
import { databases, ensureAuth } from './appwriteClient';
import { ID, Query, Client } from 'appwrite';
import { getFileUrl } from './studentService';

const DATABASE_ID = '682acd26001f69683cd8';
const ADMIN_COMMS_COLLECTION_ID = 'admin-comms';
const STUDENTS_COLLECTION_ID = '682acd33000e34dd93c0';

// Bucket ID for messaging files
const BUCKET_ID = '6862996c001bd6436274';

// Initialize client for real-time subscriptions
const client = new Client()
    .setEndpoint('https://fra.cloud.appwrite.io/v1')
    .setProject('683566a7000250cec655');

export interface ParentMessage {
  $id: string;
  message: string;
  img?: string;
  time?: string;
  userId: string;
  isUser: boolean;
}

export interface ParentUser {
  userId: string;
  fatherName: string;
  email: string;
  studentName: string;
  level: string;
  group: string;
  studentPhotoUrl?: string;
  fatherNum?: string;
  motherNum?: string;
  lastMessageTime?: string;
}

export const fetchParentMessages = async (userId?: string) => {
  try {
    await ensureAuth();
    // Use fetchAllParentMessages to handle pagination and fetch all messages
    return await fetchAllParentMessages(userId);
  } catch (error) {
    console.error('Error fetching parent messages:', error);
    throw error;
  }
};

// New function to fetch all parent messages with pagination
export const fetchAllParentMessages = async (userId?: string): Promise<any[]> => {
  try {
    await ensureAuth();
    const allMessages: any[] = [];
    let offset = 0;
    const limit = 100;
    
    while (true) {
      const queries = [
        Query.limit(limit),
        Query.offset(offset),
        Query.orderAsc('$createdAt') // Ensure consistent ordering
      ];
      
      if (userId) {
        queries.push(Query.equal('userId', userId));
      }
      
      const response = await databases.listDocuments(DATABASE_ID, ADMIN_COMMS_COLLECTION_ID, queries);
      allMessages.push(...response.documents);
      
      // If we got fewer documents than the limit, we've reached the end
      if (response.documents.length < limit) {
        break;
      }
      
      offset += limit;
    }
    
    return allMessages;
  } catch (error) {
    console.error('Error fetching all parent messages:', error);
    throw error;
  }
};

// Function to fetch messages with pagination for infinite scroll
export const fetchParentMessagesPaginated = async (
  userId?: string, 
  limit: number = 50, 
  offset: number = 0
): Promise<{ messages: any[], hasMore: boolean }> => {
  try {
    await ensureAuth();
    const queries = [
      Query.limit(limit + 1), // Fetch one extra to check if there are more
      Query.offset(offset),
      Query.orderDesc('$createdAt') // Most recent first for chat
    ];
    
    if (userId) {
      queries.push(Query.equal('userId', userId));
    }
    
    const response = await databases.listDocuments(DATABASE_ID, ADMIN_COMMS_COLLECTION_ID, queries);
    
    const hasMore = response.documents.length > limit;
    const messages = hasMore ? response.documents.slice(0, limit) : response.documents;
    
    return { messages, hasMore };
  } catch (error) {
    console.error('Error fetching paginated parent messages:', error);
    throw error;
  }
};

export const fetchRecentParentMessages = async (limit: number = 5): Promise<ParentMessage[]> => {
  try {
    await ensureAuth();
    
    // Get recent messages from parents (isUser = true) ordered by time
    const response = await databases.listDocuments(
      DATABASE_ID, 
      ADMIN_COMMS_COLLECTION_ID, 
      [
        Query.equal('isUser', true),
        Query.orderDesc('$createdAt'),
        Query.limit(limit)
      ]
    );
    
    return response.documents.map((doc: any) => ({
      $id: doc.$id,
      message: doc.message || '',
      img: doc.img,
      time: doc.time || doc.$createdAt,
      userId: doc.userId,
      isUser: doc.isUser,
    }));
  } catch (error) {
    console.error('Error fetching recent parent messages:', error);
    throw error;
  }
};

export const getStudentByEmail = async (email: string) => {
  try {
    await ensureAuth();
    const response = await databases.listDocuments(
      DATABASE_ID,
      STUDENTS_COLLECTION_ID,
      [Query.equal('email', email)]
    );
    
    if (response.documents.length > 0) {
      const student = response.documents[0];
      return {
        studentId: student.$id,
        studentName: student.name || 'Unknown',
        fatherName: student.fathers_name || student.father_name || 'Unknown',
        level: student.level || '1',
        group: student.group || 'larva1',
        studentPhotoUrl: getFileUrl(student.student_photo_url) || '',
        fatherNum: student.father_num || '',
        motherNum: student.mother_num || ''
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching student by email:', error);
    return null;
  }
};

export const addParentMessage = async (message: string, userId: string, isUser: boolean, img?: string) => {
  try {
    await ensureAuth();
    const data: { 
      message: string; 
      userId: string; 
      isUser: boolean; 
      time: string; 
      img?: string; 
    } = { 
      message, 
      userId, 
      isUser, 
      time: new Date().toISOString() 
    };
    if (img) data.img = img;
    
    const response = await databases.createDocument(
      DATABASE_ID,
      ADMIN_COMMS_COLLECTION_ID,
      ID.unique(),
      data
    );
    return response;
  } catch (error) {
    console.error('Error adding parent message:', error);
    throw error;
  }
};

export const uploadParentChatImage = async (file: File): Promise<string> => {
  try {
    await ensureAuth();
    const fileId = ID.unique();
    const upload = await cloudStorage.createFile(BUCKET_ID, fileId, file);
    return cloudStorage.getFileView(BUCKET_ID, upload.$id).toString();
  } catch (error) {
    console.error('Error uploading parent chat image:', error);
    throw error;
  }
};

export const deleteParentMessage = async (messageId: string) => {
  try {
    await ensureAuth();
    await databases.deleteDocument(DATABASE_ID, ADMIN_COMMS_COLLECTION_ID, messageId);
  } catch (error) {
    console.error('Error deleting parent message:', error);
    throw error;
  }
};

export const getParentUsers = async (): Promise<ParentUser[]> => {
  try {
    await ensureAuth();

    console.log('🔄 Loading parent users using student-based approach...');

    // Step 1: Fetch all students (this is the fixed list based on enrollment)
    const allStudents = await getAllStudents();
    console.log(`📚 Fetched ${allStudents.length} students`);

    // Step 2: Fetch recent messages to get last message metadata
    // We fetch recent messages (not all) to get last message times efficiently
    const recentMessagesLimit = 2000; // Fetch last 2000 messages to cover recent activity
    const recentMessages: any[] = [];
    let offset = 0;
    const batchLimit = 100;

    // Fetch recent messages in batches
    while (recentMessages.length < recentMessagesLimit) {
      const messagesResponse = await databases.listDocuments(
        DATABASE_ID,
        ADMIN_COMMS_COLLECTION_ID,
        [
          Query.select(['userId', 'time', '$createdAt']),
          Query.orderDesc('$createdAt'), // Get most recent first
          Query.limit(batchLimit),
          Query.offset(offset)
        ]
      );

      if (messagesResponse.documents.length === 0) {
        break; // No more messages
      }

      recentMessages.push(...messagesResponse.documents);

      if (messagesResponse.documents.length < batchLimit) {
        break; // Got all messages
      }

      offset += batchLimit;
    }

    console.log(`💬 Fetched ${recentMessages.length} recent messages`);

    // Step 3: Build a map of userId -> lastMessageTime
    // For each userId, track only the most recent message
    const userLastMessageMap = new Map<string, string>();

    recentMessages.forEach((doc: any) => {
      const userId = doc.userId;
      const messageTime = doc.time || doc.$createdAt;

      // Since messages are ordered desc by $createdAt, first occurrence is the most recent
      if (!userLastMessageMap.has(userId)) {
        userLastMessageMap.set(userId, messageTime);
      }
    });

    console.log(`👥 Found ${userLastMessageMap.size} unique users with messages`);

    // Step 4: Combine students with their message metadata
    const parentUsers: ParentUser[] = allStudents.map(student => ({
      ...student,
      lastMessageTime: userLastMessageMap.get(student.userId) || undefined
    }));

    // Step 5: Sort by most recent message timestamp
    // Students with messages appear first, sorted by time (newest first)
    // Students without messages appear last, sorted alphabetically
    parentUsers.sort((a, b) => {
      const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
      const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;

      // If both have messages or both don't, compare times (or 0s)
      if ((timeA > 0 && timeB > 0) || (timeA === 0 && timeB === 0)) {
        if (timeA !== timeB) {
          return timeB - timeA; // Descending order (newest first)
        }
        // If times are equal (or both 0), sort alphabetically by student name
        return a.studentName.localeCompare(b.studentName);
      }

      // Otherwise, those with messages come first
      return timeB === 0 ? -1 : 1;
    });

    // Only return users who have at least one message (to match original behavior)
    // If you want to show all students even without messages, remove this filter
    const usersWithMessages = parentUsers.filter(user => user.lastMessageTime);

    console.log(`✅ Returning ${usersWithMessages.length} parent users with message history`);

    return usersWithMessages;
  } catch (error) {
    console.error('💥 Error in getParentUsers:', error);
    throw error;
  }
};

export const getAllStudents = async (): Promise<ParentUser[]> => {
  try {
    await ensureAuth();

    // Get all students with pagination to handle >100 students
    const allStudentDocs: any[] = [];
    let offset = 0;
    const limit = 100;

    while (true) {
      const studentsResponse = await databases.listDocuments(
        DATABASE_ID,
        STUDENTS_COLLECTION_ID,
        [
          Query.limit(limit),
          Query.offset(offset),
          Query.orderAsc('name') // Consistent ordering
        ]
      );

      allStudentDocs.push(...studentsResponse.documents);

      // If we got fewer documents than the limit, we've reached the end
      if (studentsResponse.documents.length < limit) {
        break;
      }

      offset += limit;
    }

    const allStudents: ParentUser[] = allStudentDocs.map((student: any) => ({
      userId: student.email || '',
      fatherName: student.father_name || 'Unknown',
      email: student.email || '',
      studentName: student.name || 'Unknown',
      level: student.level || '1',
      group: student.group || 'larva1',
      studentPhotoUrl: getFileUrl(student.student_photo_url) || '',
      fatherNum: student.father_num || '',
      motherNum: student.mother_num || ''
    }));

    return allStudents;
  } catch (error) {
    console.error('Error getting all students:', error);
    throw error;
  }
};

export const subscribeToParentMessages = (
  callback: (message: ParentMessage) => void,
  selectedUserId?: string
) => {
  try {
    const unsubscribe = client.subscribe(
      `databases.${DATABASE_ID}.collections.${ADMIN_COMMS_COLLECTION_ID}.documents`,
      (response) => {
        // Only process if it's a create event
        if (response.events.includes('databases.*.collections.*.documents.*.create')) {
          const newMessage = response.payload as any;
          
          // Always pass the message, let the component decide what to do with it
          const parentMessage: ParentMessage = {
            $id: newMessage.$id,
            message: newMessage.message || '',
            img: newMessage.img,
            time: newMessage.time || newMessage.$createdAt,
            userId: newMessage.userId,
            isUser: newMessage.isUser,
          };
          callback(parentMessage);
        }
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to parent messages:', error);
    return () => {}; // Return empty function if subscription fails
  }
};